/* eslint-disable import/no-extraneous-dependencies */
import React, { forwardRef } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { Input, InputGroup, InputRightElement, Box } from '@chakra-ui/react';
import { CalendarBlank } from 'phosphor-react';
import { ptBR } from 'date-fns/locale';

import 'react-datepicker/dist/react-datepicker.css';

registerLocale('pt-BR', ptBR);

interface KanbanDatePickerProps {
  value: string;
  // eslint-disable-next-line no-unused-vars
  onChange: (value: string) => void;
  placeholder?: string;
  isClearable?: boolean;
}

const CustomInput = forwardRef<HTMLInputElement, any>(({ value, onClick, placeholder }, ref) => (
  <InputGroup size="sm">
    <Input
      ref={ref}
      value={value || ''}
      onClick={onClick}
      placeholder={placeholder}
      readOnly
      cursor="pointer"
      borderRadius="lg"
      fontSize="13px"
      bg="white"
      borderColor="gray.200"
      _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 2px rgba(66,153,225,0.1)' }}
      _placeholder={{ color: 'gray.300' }}
    />
    <InputRightElement pointerEvents="none" h="full">
      <CalendarBlank size={14} color="#A0AEC0" />
    </InputRightElement>
  </InputGroup>
));

CustomInput.displayName = 'CustomInput';

export const KanbanDatePicker: React.FC<KanbanDatePickerProps> = ({
  value,
  onChange,
  placeholder = 'Selecionar data',
  isClearable = true,
}) => {
  const dateValue = value ? new Date(`${value}T12:00:00`) : null;

  const handleChange = (date: Date | null) => {
    if (!date) {
      onChange('');
      return;
    }
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    onChange(`${y}-${m}-${d}`);
  };

  return (
    <Box
      w="100%"
      css={{
        '.react-datepicker-wrapper, .react-datepicker__input-container': { width: '100%' },
        '.react-datepicker-popper': { zIndex: 9999 },
        '.react-datepicker': {
          fontFamily: 'inherit',
          border: '1px solid #E2E8F0',
          borderRadius: '12px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          overflow: 'hidden',
        },
        '.react-datepicker__header': {
          background: '#F7FAFC',
          borderBottom: '1px solid #E2E8F0',
          padding: '10px',
        },
        '.react-datepicker__current-month': {
          fontSize: '14px',
          fontWeight: 600,
          color: '#2D3748',
        },
        '.react-datepicker__day-name': {
          color: '#A0AEC0',
          fontSize: '11px',
          fontWeight: 600,
          width: '32px',
          lineHeight: '32px',
        },
        '.react-datepicker__day': {
          width: '32px',
          lineHeight: '32px',
          borderRadius: '8px',
          fontSize: '13px',
          color: '#4A5568',
          '&:hover': { background: '#EBF8FF', color: '#2B6CB0' },
        },
        '.react-datepicker__day--selected, .react-datepicker__day--keyboard-selected': {
          background: '#3182CE !important',
          color: 'white !important',
          fontWeight: 600,
        },
        '.react-datepicker__day--today': {
          fontWeight: 700,
          color: '#3182CE',
        },
        '.react-datepicker__day--outside-month': {
          color: '#CBD5E0',
        },
        '.react-datepicker__navigation': {
          top: '10px',
        },
        '.react-datepicker__close-icon::after': {
          background: '#A0AEC0',
          fontSize: '12px',
          padding: '2px',
        },
      }}
    >
      <DatePicker
        selected={dateValue}
        onChange={handleChange}
        locale="pt-BR"
        dateFormat="dd/MM/yyyy"
        placeholderText={placeholder}
        isClearable={isClearable}
        customInput={<CustomInput />}
        popperProps={{ strategy: 'fixed' }}
        popperPlacement="bottom-start"
      />
    </Box>
  );
};
